import { createSelector } from 'reselect';
import { getActiveSection, getStudioSlice } from './page';
import { StudioSection } from '../../types/store';
import { ProductType } from '../../components/products/types';

export const getIsProductsSection = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.PRODUCTS_LOBBY,
      StudioSection.PRODUCTS_COMMISSION,
      StudioSection.PRODUCTS_ORIGINAL,
      StudioSection.PRODUCTS_ORIGINAL_UNLISTED,
      StudioSection.PRODUCTS_EXCLUSIVE,
      StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
      StudioSection.PRODUCTS_TIER,
      StudioSection.PRODUCTS_TIER_UNLISTED,
      StudioSection.PRODUCTS_PCP,
      StudioSection.PRODUCTS_PCP_UNLISTED,
      StudioSection.PRODUCTS_PREMIUM_GALLERY,
    ].includes(activeSection)
);

export const getIsDiscountsSection = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.SELLER_DISCOUNTS_ACTIVE,
      StudioSection.SELLER_DISCOUNTS_ACTIVE_AND_SCHEDULED,
      StudioSection.SELLER_DISCOUNTS_ENDED,
      StudioSection.SELLER_DISCOUNTS_SCHEDULED,
      StudioSection.SELLER_DISCOUNTS_ALL,
    ].includes(activeSection)
);

export const getIsShopReportsSection = createSelector(
  [getActiveSection],
  activeSection => activeSection === StudioSection.SHOP_REPORTS
);

export const getIsEarningsSection = createSelector(
  [getActiveSection],
  activeSection => activeSection === StudioSection.EARNINGS
);

export const getIsShopSection = createSelector(
  [
    getActiveSection,
    getIsDiscountsSection,
    getIsProductsSection,
    getIsEarningsSection,
  ],
  (activeSection, isDiscountsSection, isProductSection, isEarningsSection) =>
    isDiscountsSection ||
    isProductSection ||
    activeSection === StudioSection.SHOP_REPORTS ||
    isEarningsSection
);

export const getProductsCounts = createSelector(
  [getStudioSlice],
  state => state.products?.counts
);

export const getCurrentProductsSectionCount = createSelector(
  [getActiveSection, getProductsCounts],
  (activeSection, counts) => {
    const sectionToCount = {
      [StudioSection.PRODUCTS_COMMISSION]: counts?.commission,
      [StudioSection.PRODUCTS_ORIGINAL]: counts?.originals,
      [StudioSection.PRODUCTS_ORIGINAL_UNLISTED]: counts?.originals,
      [StudioSection.PRODUCTS_EXCLUSIVE]: counts?.exclusive,
      [StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED]: counts?.exclusive,
      [StudioSection.PRODUCTS_TIER]: counts?.tier,
      [StudioSection.PRODUCTS_TIER_UNLISTED]: counts?.tier,
      [StudioSection.PRODUCTS_PCP]: counts?.pcp,
      [StudioSection.PRODUCTS_PCP_UNLISTED]: counts?.pcp,
      [StudioSection.PRODUCTS_PREMIUM_GALLERY]: counts?.premiumGallery,
    };
    return sectionToCount[activeSection] || 0;
  }
);

export const getCurrentProductsSectionType = createSelector(
  [getActiveSection],
  activeSection => {
    switch (activeSection) {
      case StudioSection.PRODUCTS_COMMISSION:
        return ProductType.Commission;
      case StudioSection.PRODUCTS_ORIGINAL:
      case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
        return ProductType.Original;
      case StudioSection.PRODUCTS_PREMIUM_GALLERY:
        return ProductType.PremiumGallery;
      case StudioSection.PRODUCTS_EXCLUSIVE:
      case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
        return ProductType.Exclusive;
      case StudioSection.PRODUCTS_TIER:
      case StudioSection.PRODUCTS_TIER_UNLISTED:
        return ProductType.Tier;
      case StudioSection.PRODUCTS_PCP:
      case StudioSection.PRODUCTS_PCP_UNLISTED:
        return ProductType.PremiumDownload;
      default:
        return null;
    }
  }
);

export const getBoostRestrictions = createSelector(
  [getStudioSlice],
  state => state.boostRestrictions
);

export const getOriginalsData = createSelector(
  [getStudioSlice],
  state => state.originalsData
);
